import { useMemo, useState } from "react";
import {
  AlertTriangle, Eye, ScanLine, EyeOff, Layers, ListTree, MapPin, MousePointerClick, PenLine, Shapes, Building2, X, Route, Mailbox,
} from "lucide-react";
import { TILE_LAYERS, MULTI_COLOR, textOn, tourneeColor, groupColor } from "../../lib/mapStyle";
import { LivreursDeTournee } from "../livreurs/LivreurFiche";
import { TourneeSelect } from "./ZoneDialog";

export function TourneeChip({ t, onClick, size = "sm" }) {
  if (!t) return null;
  const cls = size === "lg" ? "text-base px-2.5 py-1" : "text-xs px-2 py-0.5";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={`inline-flex items-center gap-1.5 rounded-md font-mono font-semibold ${cls} ${onClick ? "hover:opacity-85" : "cursor-default"}`}
      style={{ background: t.couleur, color: textOn(t.couleur) }}
      title={t.libelle}
    >
      {t.code}
    </button>
  );
}

function Card({ children, onClose, testId }) {
  return (
    <div className="relative rounded-xl bg-card border border-border shadow-xl p-4 max-h-[46vh] md:max-h-[62vh] overflow-y-auto" data-testid={testId}>
      {onClose && (
        <button onClick={onClose} className="absolute top-2.5 right-2.5 p-1 rounded-md hover:bg-accent text-muted-foreground" aria-label="Fermer">
          <X size={16} />
        </button>
      )}
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-sm font-medium truncate">{children || "—"}</p>
    </div>
  );
}

function TourneeBlock({ t, sst, color }) {
  return (
    <div className="rounded-lg border border-border overflow-hidden" data-testid="result-tournee">
      <div className="flex items-stretch">
        <div className="w-2 shrink-0" style={{ background: color || t.couleur }} />
        <div className="p-3 flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Tournée</span>
            <span className="font-mono text-3xl font-bold leading-none" data-testid="result-tournee-code">{t.code}</span>
          </div>
          {t.libelle && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{t.libelle}</p>}
          <div className="grid grid-cols-2 gap-3 mt-3">
            <Field label="SST">{sst || t.sst}</Field>
            <Field label="Secteur">{t.secteur}</Field>
          </div>
        </div>
      </div>
    </div>
  );
}

const PRECISION = {
  adresse: ["Adresse exacte (n° + rue)", "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30"],
  rue: ["Rue trouvée", "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30"],
  commune: ["Commune", "bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30"],
};

/** Ce qui a été lu sur l'étiquette scannée. */
function ScanLu({ s }) {
  const [txt, cls] = PRECISION[s.precision] || PRECISION.commune;
  return (
    <div className="mt-3 rounded-lg border border-border bg-muted/40 p-2.5 text-xs space-y-1" data-testid="scan-lu">
      <p className="flex items-center gap-1.5 font-semibold">
        <ScanLine size={13} className="text-primary" /> Étiquette scannée
        <span className={`ml-auto px-1.5 py-0.5 rounded-full border text-[10px] ${cls}`} data-testid="scan-precision">{txt}</span>
      </p>
      {s.destinataire && <p className="font-medium">{s.destinataire}</p>}
      {s.adresse && <p>{s.adresse}</p>}
      <p className="font-semibold">{s.cp} {s.commune || s.ville}</p>
      {s.numero && <p className="font-mono text-muted-foreground">Colis {s.numero}{s.complete_par ? ` · adresse ${s.complete_par === "fiche" ? "de la fiche colis" : "du fichier transporteur"}` : ""}</p>}
      {s.alerte && <p className="text-amber-600 dark:text-amber-400 flex items-start gap-1" data-testid="scan-alerte"><AlertTriangle size={12} className="mt-0.5 shrink-0" /> {s.alerte}</p>}
    </div>
  );
}

export function PointResult({ data, droits = {}, onClose, onTournee, onEditZone, onAffecterCommune, onDrawHere, tourneesByCode, tournees, onAssignZone }) {
  const { tournee, zone, commune, source, candidates, label, cp } = data;
  return (
    <Card onClose={onClose} testId="result-card">
      <div className="flex items-start gap-2 pr-6">
        <MapPin size={18} className="text-[#ea4335] mt-0.5 shrink-0" />
        <div className="min-w-0">
          <p className="font-semibold leading-snug" data-testid="result-label">{label || "Point sélectionné"}</p>
          <p className="text-xs text-muted-foreground">
            {commune ? `${commune.nom}${cp ? ` · ${cp}` : ""}` : cp || "Hors des communes connues"} · {data.lat.toFixed(5)}, {data.lon.toFixed(5)}
          </p>
        </div>
      </div>

      {data.scan && <ScanLu s={data.scan} />}
      <div className="mt-3 space-y-3">
        {tournee && <TourneeBlock t={tournee} sst={data.sst} color={zone?.couleur} />}
        {tournee && <LivreursDeTournee code={tournee.code} tourneesByCode={tourneesByCode || {}} />}

        {source === "zone" && (
          <p className="text-xs flex items-center gap-1.5 text-muted-foreground" data-testid="result-source">
            <Shapes size={13} /> {zone.predecoupage ? "Quartier" : "Dans la zone"} <b className="text-foreground">« {zone.nom} »</b>
            <span className="w-2.5 h-2.5 rounded-sm" style={{ background: zone.couleur }} />
          </p>
        )}
        {source === "zone_non_affectee" && (
          <div className="rounded-lg border border-dashed border-border p-3 text-sm" data-testid="result-source">
            <p className="flex items-center gap-1.5">
              <Shapes size={14} className="text-muted-foreground" /> Quartier <b>« {zone.nom} »</b> — pas encore affecté
            </p>
            {candidates?.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                <span className="text-xs text-muted-foreground">Tournées possibles :</span>
                {candidates.map((t) => <TourneeChip key={t.code} t={t} onClick={() => onTournee(t.code)} />)}
              </div>
            )}
          </div>
        )}
        {droits.affecter && zone && onAssignZone && (
          <div className="rounded-lg bg-secondary/60 p-2.5 space-y-1.5" data-testid="zone-quick-assign">
            <p className="text-xs font-medium">Tournée de {zone.predecoupage ? "ce quartier" : "cette zone"}</p>
            <TourneeSelect
              tournees={tournees || []}
              value={zone.tournee}
              onChange={(code) => onAssignZone(zone, code)}
              allowNone
              noneLabel="— Non affecté —"
              testId="zone-quick-select"
            />
          </div>
        )}
        {source === "cp" && (
          <p className="text-xs flex items-center gap-1.5 text-muted-foreground" data-testid="result-source">
            <Mailbox size={13} /> Code postal {cp} desservi par une seule tournée
          </p>
        )}
        {source === "liste" && (
          <p className="text-xs flex items-center gap-1.5 text-muted-foreground" data-testid="result-source">
            <Building2 size={13} /> Village {commune?.nom} rattaché à cette tournée (liste des communes)
          </p>
        )}
        {source === "commune" && (
          <p className="text-xs flex items-center gap-1.5 text-muted-foreground" data-testid="result-source">
            <Building2 size={13} /> Commune affectée manuellement à cette tournée
          </p>
        )}
        {source === "multi" && (
          <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 text-sm" data-testid="result-source">
            <p className="flex items-start gap-2">
              <AlertTriangle size={16} className="text-amber-500 shrink-0 mt-0.5" />
              <span>
                Le code postal <b>{cp}</b> est partagé entre {candidates.length} tournées et aucune zone ne couvre cette adresse.
                {droits.dessin ? " Dessinez une zone pour la rattacher." : " Un responsable doit dessiner la zone."}
              </span>
            </p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {candidates.map((t) => <TourneeChip key={t.code} t={t} onClick={() => onTournee(t.code)} />)}
            </div>
          </div>
        )}
        {source === "non_affectee" && (
          <p className="text-sm text-muted-foreground" data-testid="result-source">
            {commune?.nom} est marquée <b className="text-foreground">non affectée</b> : aucune tournée ne dessert cette commune.
          </p>
        )}
        {source === "aucune" && (
          <p className="text-sm text-muted-foreground" data-testid="result-source">Aucune tournée chargée ne dessert ce point.</p>
        )}
        {data.autres_zones?.length > 0 && (
          <p className="text-xs text-muted-foreground">
            Zones superposées : {data.autres_zones.map((z) => `${z.nom} (${z.tournee || "—"})`).join(", ")} — la plus petite est retenue.
          </p>
        )}
        {zone?.notes && <p className="text-xs border-l-2 border-border pl-2 text-muted-foreground whitespace-pre-line">{zone.notes}</p>}

        {(droits.dessin || droits.affecter) && (
          <div className="flex flex-wrap gap-2 pt-1">
            {zone && droits.dessin && (
              <button onClick={() => onEditZone(zone.id)} className="text-xs px-2.5 py-1.5 rounded-md border border-border hover:bg-accent inline-flex items-center gap-1.5">
                <PenLine size={13} /> Modifier la zone
              </button>
            )}
            {commune && droits.affecter && (
              <button onClick={() => onAffecterCommune(commune.code)} className="text-xs px-2.5 py-1.5 rounded-md border border-border hover:bg-accent inline-flex items-center gap-1.5">
                <Building2 size={13} /> Affectation de {commune.nom}
              </button>
            )}
            {droits.dessin && <button onClick={onDrawHere} className="text-xs px-2.5 py-1.5 rounded-md border border-border hover:bg-accent inline-flex items-center gap-1.5">
              <Shapes size={13} /> Dessiner une zone
            </button>}
          </div>
        )}
      </div>
    </Card>
  );
}

export function CpResult({ data, tourneesByCode, onClose, onTournee }) {
  const noms = data.communes.features.map((f) => f.properties.nom);
  const multi = data.tournees.length > 1;
  return (
    <Card onClose={onClose} testId="result-card">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Code postal</p>
      <p className="font-mono text-2xl font-bold">{data.cp}</p>
      <p className="text-sm text-muted-foreground mt-1">{noms.length ? noms.join(", ") : "Aucune commune connue en Occitanie"}</p>
      <div className="mt-3 space-y-2">
        {data.tournees.length === 0 && <p className="text-sm text-muted-foreground">Aucune tournée pour ce code postal.</p>}
        {data.tournees.length === 1 && <TourneeBlock t={tourneesByCode[data.tournees[0].code] || data.tournees[0]} />}
        {multi && (
          <>
            <p className="text-sm flex items-start gap-2">
              <AlertTriangle size={16} className="text-amber-500 shrink-0 mt-0.5" />
              Ce code postal est desservi par {data.tournees.length} tournées selon le village :
            </p>
            <div className="space-y-1.5">
              {data.lignes.map((l, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  {l.tournee ? (
                    <TourneeChip t={tourneesByCode[l.tournee] || { code: l.tournee, couleur: "#888" }} onClick={() => onTournee(l.tournee)} />
                  ) : (
                    <span className="flex gap-1">
                      {(l.candidates || []).map((c) => <TourneeChip key={c} t={tourneesByCode[c] || { code: c, couleur: "#888" }} onClick={() => onTournee(c)} />)}
                    </span>
                  )}
                  <span className="truncate text-muted-foreground">
                    {l.libelle}{!l.tournee && (l.candidates || []).length > 1 ? " — à découper en zones" : ""}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

export function TourneeResult({ data, onClose, onCp, tourneesByCode }) {
  const t = data.tournee;
  const cps = [...new Set(data.codes_postaux)];
  return (
    <Card onClose={onClose} testId="result-card">
      <TourneeBlock t={t} />
      <div className="mt-3"><LivreursDeTournee code={t.code} tourneesByCode={tourneesByCode || {}} /></div>
      <div className="mt-3 pt-3 border-t border-border space-y-2 text-sm">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-muted-foreground mr-1">Codes postaux :</span>
          {cps.length ? cps.map((cp) => (
            <button key={cp} onClick={() => onCp(cp)} className="font-mono text-xs px-1.5 py-0.5 rounded border border-border hover:bg-accent">{cp}</button>
          )) : <span className="text-xs text-muted-foreground">aucun</span>}
        </div>
        <p className="text-xs text-muted-foreground">
          {data.communes.length} commune(s) entière(s) · {data.zones_ids.length} zone(s) dessinée(s)
        </p>
        {data.noms_communes?.length > 0 && (
          <details className="text-xs text-muted-foreground">
            <summary className="cursor-pointer hover:text-foreground">Voir les {data.noms_communes.length} villages</summary>
            <p className="leading-relaxed mt-1">{data.noms_communes.join(", ")}</p>
          </details>
        )}
      </div>
    </Card>
  );
}

export function TourneeLegend({ tournees, ctx, hidden, setHidden, onTournee, selected }) {
  const groups = useMemo(() => {
    const g = {};
    tournees.forEach((t) => {
      const key = t.hors_carte ? "Hors carte" : t.secteur || "Sans secteur";
      (g[key] = g[key] || []).push(t);
    });
    return Object.entries(g).sort(([a], [b]) => (a === "Hors carte") - (b === "Hors carte") || a.localeCompare(b));
  }, [tournees]);

  const toggle = (code) => {
    const next = new Set(hidden);
    next.has(code) ? next.delete(code) : next.add(code);
    setHidden(next);
  };

  return (
    <div className="space-y-4" data-testid="tournee-legend">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">{tournees.length} tournées</span>
        <div className="flex gap-3">
          <button className="hover:underline" onClick={() => setHidden(new Set())}>Tout afficher</button>
          <button className="hover:underline" onClick={() => setHidden(new Set(tournees.map((t) => t.code)))}>Tout masquer</button>
        </div>
      </div>
      {groups.map(([secteur, list]) => (
        <div key={secteur}>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1.5">
            {ctx.colorBy === "secteur" && secteur !== "Hors carte" && <span className="w-2.5 h-2.5 rounded-sm" style={{ background: groupColor(secteur, ctx.secteurs) }} />}
            {secteur} <span className="opacity-60">· {list.length}</span>
          </p>
          <div className="space-y-0.5">
            {list.map((t) => {
              const off = hidden.has(t.code);
              const color = tourneeColor(t.code, ctx);
              return (
                <div
                  key={t.code}
                  className={`group flex items-center gap-2 rounded-md px-1.5 py-1 ${selected === t.code ? "bg-accent" : "hover:bg-accent/60"} ${off ? "opacity-45" : ""}`}
                >
                  <button onClick={() => toggle(t.code)} className="p-0.5 text-muted-foreground hover:text-foreground" aria-label={off ? "Afficher" : "Masquer"}>
                    {off ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                  <button onClick={() => !t.hors_carte && onTournee(t.code)} className="flex items-center gap-2 min-w-0 flex-1 text-left" data-testid={`legend-${t.code}`}>
                    <span className="w-3.5 h-3.5 rounded-[4px] shrink-0 border border-black/10" style={{ background: t.hors_carte ? "transparent" : color, borderColor: t.hors_carte ? MULTI_COLOR : undefined }} />
                    <span className="font-mono text-sm font-semibold w-9 shrink-0">{t.code}</span>
                    <span className="text-xs text-muted-foreground truncate flex-1">{t.libelle}</span>
                    <span className="text-[10px] text-muted-foreground shrink-0 max-w-[80px] truncate">{t.sst}</span>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function Toggle({ label, checked, onChange }) {
  return (
    <label className="flex items-center justify-between gap-3 py-1.5 text-sm cursor-pointer">
      {label}
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-[hsl(var(--primary))]" />
    </label>
  );
}

export function DisplaySettings({ baseLayer, setBaseLayer, colorBy, setColorBy, show, setShow, onOccitanie, onFitTournees }) {
  return (
    <div className="space-y-5 text-sm">
      <div>
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Fond de carte</p>
        <div className="grid grid-cols-4 gap-1.5">
          {Object.entries(TILE_LAYERS).map(([k, v]) => (
            <button key={k} onClick={() => setBaseLayer(k)} className={`px-2 py-1.5 rounded-md border text-xs ${baseLayer === k ? "border-primary bg-primary/10 text-primary font-semibold" : "border-border hover:bg-accent"}`}>
              {v.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Colorer par</p>
        <div className="grid grid-cols-3 gap-1.5">
          {[["tournee", "Tournée"], ["secteur", "Secteur"], ["sst", "SST"]].map(([k, l]) => (
            <button key={k} onClick={() => setColorBy(k)} className={`px-2 py-1.5 rounded-md border text-xs ${colorBy === k ? "border-primary bg-primary/10 text-primary font-semibold" : "border-border hover:bg-accent"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Couches</p>
        <Toggle label="Communes (par code postal)" checked={show.communes} onChange={(v) => setShow({ ...show, communes: v })} />
        <Toggle label="Zones dessinées" checked={show.zones} onChange={(v) => setShow({ ...show, zones: v })} />
        <Toggle label="Noms des zones" checked={show.labels} onChange={(v) => setShow({ ...show, labels: v })} />
        <Toggle label="Limites des départements" checked={show.deps} onChange={(v) => setShow({ ...show, deps: v })} />
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Légende</p>
        <div className="space-y-1.5 text-xs text-muted-foreground">
          <p className="flex items-center gap-2"><span className="w-5 h-3.5 rounded-sm bg-[#4363d8]/40 border border-[#4363d8]" /> Commune entière (code postal à tournée unique)</p>
          <p className="flex items-center gap-2"><span className="w-5 h-3.5 rounded-sm border border-dashed border-[#9ca3af] bg-[#9ca3af]/10" /> Code postal partagé : à découper en zones</p>
          <p className="flex items-center gap-2"><span className="w-5 h-3.5 rounded-sm bg-[#e11d48]/45 border-2 border-[#e11d48]" /> Zone dessinée (prioritaire)</p>
          <p className="flex items-center gap-2"><MousePointerClick size={14} /> Cliquez sur la carte pour connaître la tournée d'un point</p>
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={onFitTournees} className="flex-1 text-xs px-2 py-2 rounded-md border border-border hover:bg-accent inline-flex items-center justify-center gap-1.5"><Route size={13} /> Secteurs desservis</button>
        <button onClick={onOccitanie} className="flex-1 text-xs px-2 py-2 rounded-md border border-border hover:bg-accent inline-flex items-center justify-center gap-1.5"><Layers size={13} /> Toute l'Occitanie</button>
      </div>
    </div>
  );
}

export function SidePanel({ tab, setTab, children }) {
  const [collapsed, setCollapsed] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);
  return (
    <div className="rounded-xl bg-card/95 backdrop-blur border border-border shadow-xl flex flex-col min-h-0 md:flex-1">
      <div className="flex items-center border-b border-border p-1 gap-1">
        {[["tournees", "Tournées", ListTree], ["affichage", "Affichage", Layers]].map(([k, l, Icon]) => (
          <button
            key={k}
            onClick={() => { setTab(k); setCollapsed(false); }}
            className={`flex-1 flex items-center justify-center gap-1.5 text-sm py-1.5 rounded-lg ${tab === k && !collapsed ? "bg-accent font-semibold" : "text-muted-foreground hover:text-foreground"}`}
          >
            <Icon size={14} /> {l}
          </button>
        ))}
        <button onClick={() => setCollapsed((c) => !c)} className="px-2 py-1.5 text-xs text-muted-foreground hover:text-foreground md:hidden">
          {collapsed ? "▾" : "▴"}
        </button>
      </div>
      <div className={`p-3 overflow-y-auto min-h-0 ${collapsed ? "hidden" : "max-h-[34vh] md:max-h-none md:flex-1"}`}>{children}</div>
    </div>
  );
}
